import { useEffect, useState } from "react";

import {
  Flex,
  SimpleGrid,
  VStack,
  Text,
  Center,
  Input,
  FormControl,
  FormErrorMessage,
  Select,
  useToast,
  Img,
  AspectRatio,
  Spacer,
} from "@chakra-ui/react";

//Custom Component
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import FormConfirmButton from "components/AlertDialogButton/FormConfirmButton";

//Endpoint
import { useFormik } from "formik";
import * as Yup from "yup";

// Constants
import { BANNER_TYPES } from "constants";
import { REQUEST_TEXT_STATUS, TEXT_DIALOG, BANNER_STATE } from "constants/bannersText";

// Services
import { createBanner, updateBanner } from "service/banner.service";
import {
  useHistory,
  useParams,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

export default function BannerForm() {
  // VARIABLES    ===================================================================
  const toast = useToast(); // For status message of sent request
  const location = useLocation(); // For redirect, route
  const history = useHistory(); // redirect, state
  const route_param = useParams(); // Get route parameters
  const operation = route_param.operation; // Get ':operation' parameter from route

  // Go back to the banner.js page is location.state is null
  const banner_state = location?.state ?? null;
  if (
    (operation !== "create" && banner_state === null) ||
    banner_state === "" ||
    banner_state === undefined
  ) {
    history.push({ pathname: "/admin/banner" });
    toast(BANNER_STATE["error"]);
  }
  //State
  const [inputImage, setInputImage] = useState(
    location?.state?.files[0]?.file_url ?? null
  );

  //Texts
  const reqInfo = REQUEST_TEXT_STATUS[operation]; // For status message after request is sent
  const dialog = TEXT_DIALOG[operation]; // For alert dialogs

  // Determine the operation
  let data = { id: "", name: "", type: "", image: null, operation: operation };
  if (operation === "update") {
    data.id = location?.state?.id;
    data.name = location?.state?.name;
    data.type = location?.state?.type;
    data.image = inputImage;
  }

  // FUNCTIONS    ===================================================================
  // Preview input image
  const previewImageUpdate = (e) => {
    if (e.target.files && e.target.files[0]) {
      setInputImage(URL.createObjectURL(e.target.files[0]));
      formik.setFieldValue("image", e.target.files[0]);
    }
  };

  // FORMIK       ===================================================================
  const formik = useFormik({
    initialValues: data,
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Banner name is required"),
      type: Yup.string().required("Banner type is required"),
      operation: Yup.string().oneOf(["create", "update"]),
      image: Yup.mixed().when("operation", {
        is: (value) => value === "create",
        then: () => Yup.mixed().required("Must upload banner image"),
        otherwise: () => Yup.string(),
      }),
    }),
    onSubmit: async (values) => {
      // Form data
      const formdata = new FormData();
      formdata.append("id", values.id);
      formdata.append("name", values.name);
      formdata.append("type", values.type);

      // If Image is uploaded
      if (values.image !== null && values.image instanceof File) {
        formdata.append("image", values.image);
      }

      try {
        let res = null;

        if (operation === "create") {
          res = await createBanner(formdata); // Create Banner
        } else if (operation === "update") {
          res = await updateBanner(formdata); // Update Banner
        }
        if (res) {
          toast(reqInfo.success);
        }
        // Redirect
        history.push({
          pathname: "/admin/banner",
        });
      } catch (error) {
        toast(reqInfo.error);
        // Redirect
        history.push({
          pathname: "/admin/banner",
        });
      }
    },
  });

  // RETURN RENDERS ================================================================
  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} pb="0px">
        <Spacer />

        <CardHeader p="6px 0px 22px 0px">
          <Text
            fontSize="lg"
            color="#fff"
            fontWeight="bold"
            textColor={"blue.200"}
            textTransform={"uppercase"}
          >
            {dialog.header + " Page"}
          </Text>
        </CardHeader>
        <CardBody>
          <SimpleGrid
            w={"full"}
            my={"4"}
            px={"4"}
            columns={{ base: 1, md: 2 }}
            spacing={{ base: "12", md: "12" }}
          >
            <FormControl isDisabled hidden>
              <Input name="operation" value={formik.values.operation} />
            </FormControl>
            <FormControl isInvalid={!!(formik?.errors.name && formik?.touched.name)}>
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Banner Name</Text>
                <Input
                  // {...formik.getFieldProps("title")}
                  name="name"
                  bg={"white"}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Banner Name"
                />
                <FormErrorMessage bottom={"-6"} position={"absolute"} textColor={"red"}>
                  {formik?.errors.name}
                </FormErrorMessage>
              </VStack>
            </FormControl>

            <FormControl
              isInvalid={
                !!(formik?.errors.type && formik?.touched.type && operation === "create")
              }
              isDisabled={operation === "update"}
            >
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Banner Type</Text>
                <Select
                  name="type"
                  textColor={"black"}
                  variant="app-outline"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={formik.values.type === "" && "Select Banner Type"}
                  value={formik.values.type}
                >
                  {Object.keys(BANNER_TYPES).map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage bottom={"-6"} position={"absolute"} textColor={"red"}>
                  {formik?.errors.type}
                </FormErrorMessage>
              </VStack>
            </FormControl>

            <FormControl
              isInvalid={
                !!(
                  formik?.errors.image &&
                  formik?.touched.image &&
                  operation === "create"
                )
              }
            >
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"white"}>Banner Image</Text>
                <Input
                  bg={"white"}
                  type="file"
                  name="banner_image"
                  onChange={(e) => {
                    previewImageUpdate(e);
                  }}
                />
                <FormErrorMessage bottom={"-6"} position={"absolute"} textColor={"red"}>
                  {formik.errors.image}
                </FormErrorMessage>
              </VStack>
            </FormControl>
            {inputImage !== null && (
              <AspectRatio maxHeight="400px" maxW="700px">
                <Img alt="Preview image" src={inputImage} />
              </AspectRatio>
            )}
          </SimpleGrid>
        </CardBody>

        <Center>
          <FormConfirmButton
            buttonText={operation + " Banner"}
            dialog={dialog}
            formik={formik}
          />
        </Center>
      </Card>
    </Flex>
  );
}
